import { Box, Button, styled } from "@mui/material";
import { SCAN_UPLOADED_FILE, GET_SAS } from "utils/queries";
import { Loader } from "components";
import { uploadClient } from "utils/fileuploadclient";
import React, { useState, useEffect } from "react";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import * as SasTypes from "utils/graphql/SAS";
import { useAppDispatch } from "store";
import { UploadUI } from "@progress/kendo-react-upload";
import { LocalizationProvider, loadMessages } from "@progress/kendo-react-intl";
import { useTranslation } from "react-i18next";
import portalSettingsManager from "utils/portalSettingsManager";

const LaunchUpload = styled(Button)({
  minWidth: "auto",
  padding: "6px 0px !important",
  border: "none",
  borderRadius: 0,
  "&:hover": {
    backgroundColor: "transparent",
  },
  "& .k-upload, .k-upload-button ": {
    fontSize: "10px",
    fontWeight: 800,
    height: "26px",
    width: "98px",
    color: portalSettingsManager?.buttonColors?.normal?.active?.text || "#333333",
    borderRadius: 0,
    padding: 0,
    textTransform: "uppercase",
    backgroundColor: `${portalSettingsManager?.buttonColors?.normal?.active?.back || "#ffffff"} !important`,
    "&:hover": {
      cursor: "pointer",
    },
  },
  "& .k-upload-button": {
    border: `1px solid ${portalSettingsManager?.buttonColors?.normal?.active?.border || "#333333"}`,
    "&:hover": {
      backgroundColor: `${portalSettingsManager?.buttonColors?.normal?.hover?.back || "#ffffff"} !important`,
      border: `2px solid ${portalSettingsManager?.buttonColors?.normal?.active?.border || "#333333"} !important`,
    },
  },
  "& .k-dropzone-hint, .k-upload-status-total": {
    display: "none",
  },
  "& .k-dropzone": {
    padding: "0px",
    display: "flex",
    paddingBlock: "0 !important",
    paddingInline: "0 !important",
    justifyContent: "flex-end",
  },
  "& .k-widget": {
    padding: "0px",
    border: "0px",
  },
  "& .k-upload-files": {
    display: "none",
    //border: "0px",
  },
  "& .k-file.k-state-focused": {
    boxShadow: "none",
  },
  "& .k-upload-action, .k-upload-status": {
    display: "none !important",
  },
});

const bgMessages = {
  upload: {
    select: "Upload File",
  },
};
loadMessages(bgMessages, "bg-BG");

export const UploadFile = ({ activityId, courseId, onUploadSuccess }: any): React.ReactElement => {
  const dispatch = useAppDispatch();
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const { t } = useTranslation();
  const [scanUploadedFile] = useMutation(SCAN_UPLOADED_FILE);
  const [getSasUrl] = useLazyQuery<SasTypes.SAS>(GET_SAS);

  useEffect(() => {
    const fileUploadButton = document.querySelector("[type='file']");
    if (fileUploadButton) {
      fileUploadButton.setAttribute("tabindex", "0");
    }
    // Remove invalid aria-controls attribute from Kendo upload button
    const kendoUploadButton = document.querySelector(".k-upload-button");
    if (kendoUploadButton) {
      kendoUploadButton.removeAttribute("aria-controls");
    }
  }, []);

  const fileSizeExceeded = () => {
    dispatch({
      type: "alert/show",
      payload: {
        type: "error",
        title: t("fileupload_exeeds_max_alert_title"),
        message: t("fileupload_exeeds_max_alert_message"),
      },
    });
    hideToast();
  };

  const incorrectFileExtension = () => {
    dispatch({
      type: "alert/show",
      payload: {
        type: "error",
        title: t("fileupload_incorrect_extension_alert_title"),
        message: t("fileupload_incorrect_extension_alert_message"),
      },
    });
    hideToast();
  };

  const uploadSuccess = (fileRelativePath: string) => {
    setShowLoader(false);
    scanUploadedFile({
      variables: { fileRelativePath },
    })
      .then(() => {
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("fileupload_success_alert_title"),
            message: t("fileupload_success_alert_message"),
          },
        });
        if (onUploadSuccess) {
          onUploadSuccess();
        }
        hideToast();
      })
      .catch(() => {
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("fileupload_failed_alert_title"),
            message: t("fileupload_failed_alert_message"),
          },
        });
        hideToast();
      });
  };

  const uploadFailure = () => {
    setShowLoader(false);
    dispatch({
      type: "alert/show",
      payload: {
        type: "error",
        title: t("fileupload_failed_alert_title"),
        message: t("fileupload_error_message"),
      },
    });
    hideToast();
  };

  const hideToast = () => {
    setTimeout(function () {
      dispatch({ type: "alert/hide" });
    }, 5000);
  };

  const onAdd = async (e: any) => {
    const file = e[0];
    if (file.size > Number(process.env.REACT_APP_MAXIMUM_FILE_SIZE)) {
      fileSizeExceeded();
      return;
    }
    const fileExtension = file.name.replace(/.*\./, "").toLowerCase();
    if (["txt", "docx", "pdf", "doc"]?.indexOf(fileExtension) < 0) {
      incorrectFileExtension();
      return;
    }

    setShowLoader(true);

    try {
      // Get SAS URL with the actual filename
      const sasResult = await getSasUrl({
        variables: {
          parameters: {
            programmeId: courseId,
            activityId: activityId,
            originalFileName: file.name,
          },
        },
      });

      const sasData = sasResult?.data?.sas;
      if (!sasData || !sasData.fileRelativePath) {
        uploadFailure();
        return;
      }

      // Upload file using the SAS URL
      uploadClient.upload(
        sasData,
        file,
        () => uploadSuccess(sasData.fileRelativePath),
        uploadFailure,
      );
    } catch {
      uploadFailure();
    }
  };

  return (
    <LaunchUpload
      tabIndex={-1}
      variant="outlined"
      size="small"
      aria-label="Upload File"
      title={t("fileupload_msg")}
    >
      <Box>
        <LocalizationProvider language={"bg-BG"}>
          <UploadUI onAdd={onAdd} groupedFiles={{}} />
        </LocalizationProvider>
      </Box>
      <Loader isDisplay={showLoader} message={t("fileupload_loader")}></Loader>
    </LaunchUpload>
  );
};
